import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSubscription } from "@/hooks/useSubscription";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { toast } from "sonner";
import { useState } from "react";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: activeSub } = useActiveSubscription();
  const { openCheckout } = usePaddleCheckout();
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  const { data: plans } = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("billing_plans")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data as any[];
    },
  });

  const handleSelect = async (plan: any) => {
    if (!user) return navigate("/auth?redirect=/pricing");
    if (plan.price_cents === 0) {
      toast.info("You're on the free plan");
      return;
    }
    if (!plan.paddle_price_id) {
      toast.error("This plan isn't available for checkout yet.");
      return;
    }
    setBusyPlan(plan.id);
    try {
      await openCheckout({
        priceId: plan.paddle_price_id,
        customerEmail: user.email ?? undefined,
        customData: { userId: user.id },
        successUrl: `${window.location.origin}/account/billing?checkout=success`,
      });
    } catch (e: any) {
      toast.error("Checkout failed", { description: e.message });
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <main id="main" className="container max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" /> Membership
          </Badge>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-3">
            Free for Alabama communities. Paid for institutions.
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Member is free forever for community builders. Member Pro unlocks the
            full research stack for consultants and organizations. Intelligence
            licenses our aggregated data and reports to funders and agencies.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {plans?.map((plan) => {
            const isCurrent = activeSub?.plan_id === plan.id;
            const features = Array.isArray(plan.features) ? plan.features : [];
            const highlighted = plan.slug === "shareholder_pro";
            return (
              <div
                key={plan.id}
                className={`rounded-2xl border p-6 flex flex-col ${
                  highlighted
                    ? "border-primary/60 bg-primary/5"
                    : "border-border bg-card"
                }`}
              >
                {highlighted && (
                  <Badge className="self-start mb-3 bg-primary text-primary-foreground">
                    Most popular
                  </Badge>
                )}
                <h2 className="font-display text-xl font-semibold text-foreground">
                  {plan.name}
                </h2>
                <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">
                  {plan.description}
                </p>
                <div className="mt-4 mb-6">
                  <span className="text-4xl font-bold text-foreground">
                    ${(plan.price_cents / 100).toLocaleString("en-US")}
                  </span>
                  <span className="text-sm text-muted-foreground ml-1">
                    /{plan.interval}
                  </span>
                  {plan.slug === "intelligence" && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Annual discount available — contact sales.
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {features.map((f: string) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/90">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={highlighted ? "default" : "outline"}
                  disabled={isCurrent || busyPlan === plan.id}
                  onClick={() => handleSelect(plan)}
                >
                  {isCurrent
                    ? "Current plan"
                    : busyPlan === plan.id
                      ? "Processing…"
                      : plan.price_cents === 0
                        ? "Free forever"
                        : "Start free trial"}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card/50 p-6 md:p-8 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground">
              Sponsorships & grants
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Underwrite the platform and reach the Alabama civic ecosystem.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/sponsorships">
              Explore sponsorship <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Payments are running in test mode while we finalize our processor.
        </p>
      </main>
    </div>
  );
}